import type { Ctx } from 'boardgame.io';
import { debug } from '../utils/debug';
import type { AoSGameState, SeattleGameState, SeattlePlayer, PlayerId, TrackTile, TileType, TrackOwnership } from '../types';
import { PLAYER_COLORS } from '../types';
import { SEATTLE_MAP_CONFIG, SEATTLE_RULES } from '../../data/map/seattle_metro/config';
import { loadSeattleMap, loadSeattlePreLaidTracks } from './utils/mapLoader';
import { initializeSeattleCubes, placeSeattleInitialGoods } from './utils/goodsSetup';
import { TILE_DEFINITIONS } from './constants/tiles';
import { detectPreLaidCompletedLinks } from './utils/linkCalculator';

/**
 * 敷設済み線路を配置
 * @param map - マップ全体（Hex配列）
 * @param preLaidTracks - 敷設済み線路データ
 * @returns 更新されたマップ
 */
function placePreLaidTracks(
  map: SeattleGameState['map'],
  preLaidTracks: ReturnType<typeof loadSeattlePreLaidTracks>
): SeattleGameState['map'] {
  const updatedMap = [...map];
  let tileIdCounter = 0;

  for (const track of preLaidTracks) {
    const hex = updatedMap.find(
      h => h.coordinate.q === track.hexCoord.q && h.coordinate.r === track.hexCoord.r
    );

    if (!hex) {
      console.warn(`[placePreLaidTracks] Hex not found: (${track.hexCoord.q}, ${track.hexCoord.r})`);
      continue;
    }

    // タイル定義からconnectionsを取得
    const tileDefinition = TILE_DEFINITIONS[track.tileType as TileType];
    if (!tileDefinition) {
      console.warn(`[placePreLaidTracks] Unknown tile type: ${track.tileType}`);
      continue;
    }

    // 回転を適用したconnectionsを計算
    const rotatedConnections = tileDefinition.connections.map(
      conn => conn.map(d => ((d + track.rotation) % 6) as 0 | 1 | 2 | 3 | 4 | 5)
    );

    // 単線か複線かを判定（connections配列の長さで判定）
    const numConnections = tileDefinition.connections.length;
    const isSingleTrack = numConnections === 1;

    // 複線の場合は各線路の所有権を初期化（所有者なし）
    const owners: TrackOwnership[] | undefined = !isSingleTrack
      ? Array.from({ length: numConnections }, (_, i) => ({
          playerId: '' as PlayerId,
          connectionIndex: i
        }))
      : undefined;

    // タイルを配置
    const tile: TrackTile = {
      id: `preLaid-${tileIdCounter++}`,
      type: track.tileType,
      coordinate: track.hexCoord,
      rotation: track.rotation,
      owner: '',  // 所有者なし
      placedTurn: 0,  // ゲーム開始前に配置
      connections: isSingleTrack
        ? rotatedConnections[0]
        : rotatedConnections,
      owners  // 複線の場合は各線路の所有権情報を保持
    };

    hex.tile = tile;
  }

  return updatedMap;
}

/**
 * Seattle Metro Soloプレイ用のゲーム初期状態を生成
 * @param _ctx Boardgame.io コンテキスト（未使用）
 * @returns 初期ゲーム状態（SeattleGameState）
 */
export function setupSeattleGame(_ctx: Ctx): SeattleGameState {
  // Seattle Solo Play: プレイヤーは常に1人（ID='0'）
  const playerId: PlayerId = '0';

  // Seattleプレイヤー初期化
  const soloPlayer: SeattlePlayer = {
    id: playerId,
    name: 'Solo Player',
    color: PLAYER_COLORS[0],
    cash: SEATTLE_RULES.initialCash,           // 初期資金 $10
    incomeLevel: 0,                            // 収入レベル 0
    sharesIssued: SEATTLE_RULES.initialShares, // 初期株式数 2
    locomotiveLevel: SEATTLE_RULES.initialLocoLevel, // 初期機関車レベル 1リンク
    selectedAction: null,                      // 未選択
    actionsSelectedThisTurn: [],               // 今ターン選択したアクション
    hasPassed: false,                          // アクション選択をパスしたか
    playerOrder: 1,                            // ソロプレイなので常に1
    isBankrupt: false,                         // 倒産していない
    orderBidAmount: 0,                         // Phase 2未使用（Seattleではスキップ）
    tilesPlacedThisTurn: 0,                    // Phase 4: 今ターン配置したタイル数
    hasPassedBuild: false,                     // Phase 4: 手番終了フラグ
    buildTrackHistory: [],                     // Phase 4: Cancel操作用の配置履歴
    hasUpgradedLocoThisTurn: false,            // Phase 5: 今ターン機関車強化済みフラグ
    goodsTransportedThisPhase: 0,              // Phase 5: 輸送回数
    turnsCompletedThisPhase: 0,                // Phase 5: 完了した手番数
    goodsPlacedThisTurn: 0,                    // Phase 9: 今ターン配置した商品数
    newCityTilesPlacedThisTurn: 0,             // Phase 4: 今ターン配置した新都市タイル数

    // Seattle固有プロパティ
    currentTurnSharesIssued: 0,                // 今ターン発行した株式数
    hasExecutedIssueSharesMove: false,         // 株式発行Move実行済みフラグ
    engineerUsageCount: 0,                     // Engineerアクション使用回数
    locomotiveUsageCount: 0,                   // Locomotiveアクション使用回数
    locomotiveEffectThisTurn: undefined,       // 今ターンのLocomotive効果（Cancel用）
    deliveredCubes: 0,                         // Downtown/Airportに輸送し取り除いた商品数
    finalScore: null,                          // 最終スコア（ゲーム終了前はnull）
    isVictorious: false                        // 勝利フラグ
  };

  // プレイヤーマップ
  const players: Record<PlayerId, SeattlePlayer> = {
    [playerId]: soloPlayer
  };

  // マップデータ読み込み（Seattle）
  let map = loadSeattleMap();

  // 敷設済み線路の配置（Downtown-Airport間）
  const preLaidTracks = loadSeattlePreLaidTracks();
  map = placePreLaidTracks(map, preLaidTracks);

  // 商品キューブ初期化（全5色）
  let cubesInBag = initializeSeattleCubes();

  // マップ上に初期商品配置（10の Town に各1個）
  const { updatedMap, remainingCubes } = placeSeattleInitialGoods(map, cubesInBag);
  map = updatedMap;
  cubesInBag = remainingCubes;

  // 敷設済み線路で形成される完成リンクを検出（Downtown-Airport間など）
  // 一時的なゲーム状態を使ってリンクを検出する
  const tempG = { map, completedLinks: [] } as unknown as AoSGameState;
  const preLaidLinks = detectPreLaidCompletedLinks(tempG);

  debug.log(`[setupSeattleGame] Map loaded: ${map.length} hexes, ${cubesInBag.length} cubes in bag`);
  debug.log(`[setupSeattleGame] Pre-laid tracks: ${preLaidTracks.length} tiles placed`);
  debug.log(`[setupSeattleGame] Pre-laid completed links: ${preLaidLinks.length}`);
  debug.log(`[setupSeattleGame] Player initialized: cash=$${soloPlayer.cash}, shares=${soloPlayer.sharesIssued}`);

  // Seattleゲーム状態初期化
  return {
    mapName: 'Seattle Metro Solo',
    mapConfig: SEATTLE_MAP_CONFIG,
    players,
    map,
    goodsDisplay: null,                  // Seattle: 商品ディスプレイ不使用
    cubesInBag,
    removedCubes: [],
    completedLinks: preLaidLinks,         // 敷設済み線路から検出された完成リンク
    unfinishedSections: [],              // 未完成区間なし
    ferryLinks: [],                      // Seattle: フェリーリンクなし
    adjacentLinks: [],                   // Seattle: 隣接リンクなし
    isGameOver: false,                   // ゲーム継続中
    undoCount: 0,                        // Undo to Turn Start 実行回数
    endReason: null,
    finalScores: {},

    // Seattle固有プロパティ
    cargoTransportLevel: SEATTLE_RULES.initialCargoTransportLevel, // 貨物輸送レベル初期値 2
    maxDeliveriesThisPhase: SEATTLE_RULES.initialCargoTransportLevel, // Phase 5 輸送上限（cargoTransportLevelと同期）
    actualTurn: 0,                       // ゲームターン初期値は0（Phase 1で+1される）
    goodsAtDowntown: [],                 // Downtownに留置した商品（無制限）
    goodsAtAirport: [],                  // Airportに留置した商品（最大3個）

    // 自動フェーズ用
    autoPhaseResult: null,               // 自動フェーズ処理結果
    completedAutoPhases: [],             // 完了した自動フェーズ履歴

    // Town Disk 管理
    townDisksRemaining: 8,               // Town Disk 残り枚数（初期値8）
    townDiskPlaced: {},                  // 各 Town への配置記録

    // ML学習用
    moveLog: []                          // Move-Level Replay Log
  };
}
